import { useCallback } from 'react';
import Debug from 'debug';

import { ServiceTypes } from '~/types';

import { SpotifyAuthResultSuccess } from '~/pages/api/spotify/auth/callback';
import { DeezerAuthResultSuccess } from '~/pages/api/deezer/auth/callback';
import { AmazonAuthResultSuccess } from '~/pages/api/amazon/auth/callback';

import { useAmazonMusicAuth } from './useAmazonMusicAuth';
import { useSpotifyAuth } from './useSpotifyAuth';
import { useDeezerAuth } from './useDeezerAuth';
import { ServiceAuthProps } from './types';

import {
  useAppleMusicAuth,
  AppleMusicAuthResultSuccess,
} from './useAppleMusicAuth';

const debug = Debug('songwhip/useServiceAuth');

export interface StartServiceAuthParams {
  serviceType: ServiceTypes;
  userEmail?: string;
  fallbackUrl: string;
}

export const useServiceAuth = ({
  albumId,

  onSuccess,
  onError,
}: ServiceAuthProps<
  | AmazonAuthResultSuccess
  | DeezerAuthResultSuccess
  | SpotifyAuthResultSuccess
  | AppleMusicAuthResultSuccess
>) => {
  const startSpotifyAuth = useSpotifyAuth({
    albumId,

    onSuccess,
    onError,
  });

  const startAppleMusicAuth = useAppleMusicAuth({
    albumId,

    onSuccess,
    onError,
  });

  const startDeezerAuth = useDeezerAuth({
    albumId,

    onSuccess,
    onError,
  });

  const startAmazonMusicAuth = useAmazonMusicAuth({
    albumId,

    onSuccess,
    onError,
  });

  return useCallback(
    ({ serviceType, userEmail, fallbackUrl }: StartServiceAuthParams) => {
      // WARN: these popups have to be triggered sync in response to
      // trusted user click in order to not be popup-blocked
      switch (serviceType) {
        case ServiceTypes.SPOTIFY:
          startSpotifyAuth({ fallbackUrl });

          break;
        case ServiceTypes.ITUNES:
          // when in a webview this will trigger a hard navigation away
          // from songwhip to authorize.apple.com, but in a proper browser
          // (safari/chrome/etc) this will open a popup window
          startAppleMusicAuth(userEmail).catch((error) => {
            debug('apple music presave failed', error);
          });

          break;
        case ServiceTypes.DEEZER:
          startDeezerAuth({ fallbackUrl });

          break;
        case ServiceTypes.AMAZON_MUSIC:
          startAmazonMusicAuth({ fallbackUrl });

          break;
        default:
          throw new Error(`Unsupported service type: ${serviceType}`);
      }
    },
    []
  );
};
